import styled, { css } from "styled-components";

import colors, { transparentBg } from "core/colors";
import breakpoints from "core/breakpoints";
import CircleButton from "core/CircleButton";
import BodyText from "core/BodyText";
import IconButton from "core/IconButton";
import { PopoverTriggerButton } from "core/Popover/styles";

import { easingSeconds, rapidEasing } from "core/animation";
import { focusOutline } from "core/links";

//helpers
import { convertPxToRem } from "frontend/components/helpers";

export const IconEditorContainer = styled.div`
    position: relative;
    /* all breakpoints css: */
    background: ${colors.primary.diamond};
    width: 100%;
    margin: 0 auto;

    /* tablet + desktop: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        padding: 1.5rem 0; /* 24px 0px */
        width: 30rem; /* 480px */
    }

    /* only desktop: */
    @media (min-width: ${breakpoints.desktop.rem}) {
        width: 33.63rem; /* 538px */
    }
`;

export const IconContainer = styled.div.attrs(({ color }) => ({
    style: {
        backgroundColor: color ? color : colors.secondary.onyx
    }
}))`
    /* all breakpoints css: */
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    ${({ color }) =>
        !color &&
        css`
            z-index: 0;
            background-size: 20px;
            ${transparentBg}
        `};

    /* only mobile: */
    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        height: ${({ browserWidth }) => convertPxToRem({ px: browserWidth })};
    }

    /* only tablet: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        width: 23.5rem; /* 376px */
        height: 23.5rem; /* 376px */
    }
`;

export const Icon = styled.div`
    background-image: ${({ backgroundImage }) => backgroundImage};
    background-size: 100% 100%;
    height: 100%;
    width: 100%;

    /* only mobile: */
    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        min-height: ${convertPxToRem({ px: 195.3 })};
    }
`;

export const EditingDetailsContainer = styled.div`
    /* all breakpoints css: */
    margin: 1.5rem auto 0 auto; /* 24px top margin */
    ${({ show = true }) =>
        !show &&
        css`
            display: none;
        `}

    /* only mobile: */
    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        display: none;
    }

    /* tablet + desktop */
    @media (min-width: ${breakpoints.tablet.rem}) {
        width: 23.5rem; /* 376px */
    }
`;

export const EditingDetailsTopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-content: center;
    align-items: center;
    margin-bottom: 1rem; /* 16px */
`;

export const EditingDetailsBottomRow = styled.div`
    height: ${convertPxToRem({ px: 50 })};
    display: flex;
    align-items: flex-start;
    text-align: left;
    padding-bottom: ${convertPxToRem({ px: 8 })};
`;

export const EditingColumn = styled.div`
    width: 40%;
    flex-basis: 40%;
    height: 100%;

    &:last-of-type {
        width: 20%;
        flex-basis: 20%;
        display: flex;
        justify-content: flex-end;
        align-items: flex-end;
    }
`;

export const EditingBodyText = styled(BodyText).attrs({
    type: "div",
    size: "xs"
})`
    padding-bottom: ${convertPxToRem({ px: 4 })};
`;

export const EditingControlContent = styled.div`
    display: flex;
    align-items: center;
    margin-right: ${convertPxToRem({ px: 16 })};

    &:last-of-type {
        margin-right: 0;
    }

    ${({ resetColumn }) =>
        resetColumn &&
        css`
            text-align: right;
            display: block;
            width: 100%;
        `}

    ${({ shapes }) =>
        shapes &&
        css`
            ${PopoverTriggerButton} {
                padding-top: ${convertPxToRem({ px: 3 })};
                padding-bottom: ${convertPxToRem({ px: 3 })};
                line-height: 0;
            }
        `}
`;

export const RotateIconContainer = styled.div`
    display: flex;
    margin-right: ${convertPxToRem({ px: 6 })};
    &:last-of-type {
        margin-right: 0;
    }
`;

export const RotateIconButton = styled(IconButton)`
    svg path {
        fill: ${colors.secondary.oolong};
    }

    // only show focus on keyboard not with mouse:
    &:focus:not(.focus-visible),
    &:active {
        outline: none;
        box-shadow: none;
        border: none;
    }
`;

export const EditorFlipButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-decoration: none;
    line-height: 0;
    transition: all ${rapidEasing};

    // only show focus on keyboard not with mouse:
    &:focus:not(.focus-visible),
    &:active {
        outline: none;
        box-shadow: none;
        border: none;
    }

    &:focus {
        ${focusOutline}
    }
`;

// fort links:
export const FortLinksContainer = styled.div`
    position: absolute;
    z-index: 99;

    ${CircleButton} {
        display: flex;

        &:first-of-type {
            margin-bottom: ${convertPxToRem({ px: 16 })};
        }
    }
`;

export const StyledProcessingScreen = styled.div`
    position: absolute;
    background-color: #ffffff;
    opacity: 0.6;
    width: 100%;
    height: 100%;
`;

export const ProcessingAnimationContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    border-radius: 50%;
    background-color: ${colors.secondary.vampire};
    width: ${convertPxToRem({ px: 48 })};
    height: ${convertPxToRem({ px: 48 })};
`;

/* -------------------------------------------------------------------------- */
/*                               SHAPE CONTROLS                               */
/* -------------------------------------------------------------------------- */

const shapeIconColor = ({ color }) => color || colors.secondary.oolong;

const ShapeIcon = styled.div`
    display: inline-block;
    background-color: ${shapeIconColor};
    width: ${convertPxToRem({ px: 10.5 })};
    height: ${convertPxToRem({ px: 10.5 })};
`;

export const ShapeIconSquare = styled(ShapeIcon)``;

export const ShapeIconRounded = styled(ShapeIcon)`
    border-radius: 3px;
`;

export const ShapeIconCircle = styled(ShapeIcon)`
    border-radius: 50%;
`;

export const ShapeIconRing = styled(ShapeIcon)`
    background-color: transparent;
    border: 2px solid ${shapeIconColor};
    border-radius: 50%;
`;

export const ShapeStatusContainer = styled.div`
    display: flex;
    align-items: center;

    ${ShapeIcon} {
        position: relative;
        margin-right: ${convertPxToRem({ px: 4 })};
        top: ${convertPxToRem({ px: 1 })};
        transition: all ${easingSeconds};
    }

    ${BodyText} {
        color: ${colors.secondary.oolong};
        text-decoration: none;
        transition: all ${easingSeconds};
    }

    &:hover {
        ${ShapeIcon} {
            background-color: ${colors.secondary.orca};
            transition: all ${easingSeconds};
        }

        ${ShapeIconRing} {
            background-color: transparent;
            border-color: ${colors.secondary.orca};
        }

        ${BodyText} {
            color: ${colors.secondary.orca};
            text-decoration: underline;
            transition: all ${easingSeconds};
        }
    }
`;

export const ShapeListOptionContainer = styled.div`
    text-align: left;
    cursor: pointer;

    label {
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        width: ${convertPxToRem({ px: 89 })};
        padding: ${convertPxToRem({ px: 4 })} ${convertPxToRem({ px: 8 })};

        ${BodyText} {
            color: ${colors.secondary.oolong};
        }

        ${ShapeIconCircle},
        ${ShapeIconRounded},
        ${ShapeIconSquare} {
            background-color: ${colors.secondary.oolong};
        }

        ${ShapeIconRing} {
            border-color: ${colors.secondary.oolong};
        }
    }

    input[type="radio"] {
        opacity: 0;
        position: fixed;
        width: 0;

        &:checked + label {
            background-color: ${colors.primary.diamond};

            ${BodyText} {
                color: ${colors.secondary.orca};
            }

            ${ShapeIconCircle},
            ${ShapeIconRounded},
            ${ShapeIconSquare} {
                background-color: ${colors.secondary.orca};
            }


            ${ShapeIconRing} {
                border-color: ${colors.secondary.orca};
            }
        }

        &:hover + label {
            background-color: ${colors.secondary.orca};

            ${BodyText} {
                color: ${colors.primary.diamond};
            }

            ${ShapeIconCircle},
            ${ShapeIconRounded},
            ${ShapeIconSquare} {
                background-color: ${colors.primary.diamond};
            }


            ${ShapeIconRing} {
                border-color: ${colors.primary.diamond};
            }
        }
    }
`;

export const RemoveShapeButton = styled.button`
    cursor: pointer;
    border: none;
    padding: 0;
    margin-left: ${convertPxToRem({ px: 4 })};

    // only show focus on keyboard not with mouse:
    &:focus:not(.focus-visible),
    &:active {
        outline: none;
        box-shadow: none;
        border: none;
    }

    &:focus {
        ${focusOutline}
    }
`;
